import {Fragment, useCallback, useEffect, useMemo} from 'react';
import {Link} from "react-router-dom";
import {useRecalculationJobs} from "../../../../../api/useRecalculationJobs";
import {DataGrid, GridColDef, GridSortDirection} from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import {RecalculationJob} from "../../../../../types/RecalculationJob";
import {useStopRecalculationJob} from "../../../../../api/useStopRecalculationJob";
import {LINKS} from "../../../links";
import {useUpdateActiveRecalculationJobStatus} from "../../../../../api/useUpdateActiveRecalculationJobStatus";
import {CircularProgress} from "@mui/material";
import {getCommonColumnProperties} from "../../../utils/tableUtils";


export function RecalculationJobs() {
    const {
        recalculationJobStatuses,
        loading: loadingRecalculationJobs,
        error: recalculationJobsError,
        refetch
    } = useRecalculationJobs();
    const [stopRecalculationJob, {loading: stoppingJob, error: stopJobError}] = useStopRecalculationJob();
    const [updateActiveRecalculationJobStatus] = useUpdateActiveRecalculationJobStatus();
    const isLoading = loadingRecalculationJobs;
    const hasError = !!recalculationJobsError || !!stopJobError;

    const handleChange = useCallback((recalculationJobId: string) => {
        stopRecalculationJob(recalculationJobId);
        refetch();
    }, [stopRecalculationJob, refetch]);

    const hasActiveJob = useMemo(() => {
        if (!recalculationJobStatuses || recalculationJobStatuses.jobs.length === 0) {
            return false;
        }

        return recalculationJobStatuses.jobs.some(j => j.status === 'ACTIVE');
    }, [recalculationJobStatuses]);

    const getActiveJobProgress = () => {
        if (!recalculationJobStatuses || recalculationJobStatuses.jobs.length === 0 || !hasActiveJob) {
            return 'N/A';
        }

        const totalTests = recalculationJobStatuses.jobs.find(
            j => j.status === 'ACTIVE')!.theoreticalNumberOfTestsToStart;
        const completedTests = recalculationJobStatuses!.activeJobCompletedTestCount;

        if (totalTests === 0 || completedTests === 0) {
            return '0%';
        }

        return `${(completedTests * 100 / totalTests).toFixed(1)}%`;
    }

    useEffect(() => {
        const timer = setInterval(() => {
            if (hasActiveJob) {
                updateActiveRecalculationJobStatus();
                refetch();
            }
        }, 20000);
        return () => clearInterval(timer);
    });

    const baseColumns: GridColDef[] = [
        {field: 'recalculationJobId', width: 300},
        {field: 'createdAt', type: 'date'},
        {field: 'status'},
        {field: 'createdBy'},
        {field: 'targetBefore', type: 'date'},
        {field: 'targetAfter', type: 'date'},
        {field: 'targetRecalculatedBefore', type: 'dateTime', width: 300},
        {field: 'theoreticalNumberOfTestsToStart', width: 300},
        {field: 'actualNumberOfTestsToStart', width: 250},
        {field: 'numberOfQueuedTests'},
        {
            field: 'button',
            headerName: 'actions',
            renderCell: (p: any) => (
                <Button
                    color={'primary'}
                    variant={'outlined'}
                    disabled={p.row.status !== 'ACTIVE'}
                    onClick={handleChange.bind(null, p.row.id)}
                >
                    {stoppingJob ? <CircularProgress size={23.5}/> : 'Stop job'}
                </Button>
            )
        }
    ];
    const columns: GridColDef[] = baseColumns.map(cell => ({...getCommonColumnProperties({width: 200}), ...cell}));

    const getRows = useCallback((jobs: RecalculationJob[]) => {
        return jobs.map((job: RecalculationJob) => {
            return Object.assign({'id': job.recalculationJobId}, job)
        });
    }, []);

    const newRecalculationJobUrl = useMemo(() => {
        const baseUrl = LINKS.newRecalculationJob();
        return `${baseUrl}?hasActiveJob=${hasActiveJob}`;
    }, [hasActiveJob]);

    return (
        <div>
            <h2>List of recalculation jobs!</h2>
            <div>
                <Link to={newRecalculationJobUrl}>
                    Create new job
                </Link>
            </div>
            {isLoading && <h3>Loading</h3>}
            {hasError && <h3>{recalculationJobsError} {stopJobError}</h3>}
            {!recalculationJobsError && (
                <Fragment>
                    <p>
                        Active job progress: <b>{getActiveJobProgress()}</b>
                    </p>
                    <DataGrid
                        rows={getRows(recalculationJobStatuses ? recalculationJobStatuses.jobs : [])}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                            sorting: { sortModel: [{field: 'createdAt', sort: 'desc' as GridSortDirection}] },
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}
                        autoHeight
                        disableRowSelectionOnClick={true}
                        loading={loadingRecalculationJobs}
                    />
                </Fragment>
            )}
        </div>
    );
}
